import './wdyr';

import { ApolloClient, ApolloLink, createHttpLink, InMemoryCache } from '@apollo/client';
import { Client as BelfastClient } from '@kaizenplatform/belfast';
import { createBrowserHistory } from 'history';
import TimeAgo from 'javascript-time-ago';
import en from 'javascript-time-ago/locale/en.json';
import ja from 'javascript-time-ago/locale/ja.json';
import LogRocket from 'logrocket';
import * as NewRelicBrowser from 'new-relic-browser';
import { createElement } from 'react';
import { hydrate, render } from 'react-dom';
import { addLocaleData } from 'react-intl';
import * as enLocaleData from 'react-intl/locale-data/en';
import * as jaLocaleData from 'react-intl/locale-data/ja';
import Store from 'repatch';

import GraphQLClient from './adapter/GraphQLClient';
import HttpClient from './adapter/HttpClient';
import Config from './core/Config';
import Dependency from './core/Dependency';
import State from './core/State';
import baseInitialState from './initialState';
import createKukerEmitterMiddleware from './kukerEmitterMiddleware';
import ApiErrorHandler from './service/ApiErrorHandler';
import ErrorPresenter from './service/ErrorPresenter';
import ErrorReporter from './service/ErrorReporter';
import FacebookOauthService from './service/FacebookOauthService';
import FileHandler from './service/FileHandler';
import GoogleOauthService from './service/GoogleOauthService';
import IntercomService from './service/IntercomService';
import { LogRocketService } from './service/LogRocketService';
import MessageDispatcher from './service/MessageDispatcher';
import AcctWebApi from './service/persistent/AcctWebApi';
import AdAnalyticsDailyPerformanceWebApi from './service/persistent/AdAnalyticsDailyPerformanceWebApi';
import AdAnalyticsPlacementTypesWebApi from './service/persistent/AdAnalyticsPlacementTypesWebApi';
import AdAnalyticsReportWebApi from './service/persistent/AdAnalyticsReportWebApi';
import AdAnalyticsVideoWebApi from './service/persistent/AdAnalyticsVideoWebApi';
import AdSearchWebApi from './service/persistent/AdSearchWebApi';
import AmazonAdWebApi from './service/persistent/AmazonAdWebApi';
import AuthenticationWebApi from './service/persistent/AuthenticationWebApi';
import ConfigurationStorage from './service/persistent/ConfigurationStorage';
import CreationSetWebApi from './service/persistent/CreationSetWebApi';
import CreationWebApi from './service/persistent/CreationWebApi';
import CreativePerformanceCreativeWebApi from './service/persistent/CreativePerformanceCreativeWebApi';
import DownloadApi from './service/persistent/DownloadApi';
import FacebookAdWebApi from './service/persistent/FacebookAdWebApi';
import FilesWebApi from './service/persistent/FilesWebApi';
import GoogleAdWebApi from './service/persistent/GoogleAdWebApi';
import ModificationRequestWebApi from './service/persistent/ModificationRequestWebApi';
import OrganizationWebApi from './service/persistent/OrganizationWebApi';
import PerformanceReportWebApi from './service/persistent/PerformanceReportWebApi';
import ProjectChangeWebApi from './service/persistent/ProjectChangeWebApi';
import ProjectTypeWebApi from './service/persistent/ProjectTypeWebApi';
import ProjectWebApi from './service/persistent/ProjectWebApi';
import SearchAssetApi from './service/persistent/SearchAssetApi';
import TiktokAdWebApi from './service/persistent/TiktokAdWebApi';
import UserRegistrationWebApi from './service/persistent/UserRegistrationWebApi';
import UserWebApi from './service/persistent/UserWebApi';
import ViewVisibilityStorage from './service/persistent/ViewVisibilityStorage';
import YahooAdWebApi from './service/persistent/YahooAdWebApi';
import YoutubeWebApi from './service/persistent/YoutubeWebApi';
import TranslationBackend from './service/TranslationBackend';
import setLocation from './thunk/setLocation';
import AcctUsecase from './usecase/AcctUsecase';
import AdSearchUsecase from './usecase/AdShowCaseUsecase';
import AuthenticationUsecase from './usecase/AuthenticationUsecase';
import ConfigurationChangingUsecase from './usecase/ConfigurationChangingUsecase';
import DeleteProjectUsecase from './usecase/DeleteProjectUsecase';
import EditProjectUsecase from './usecase/EditProjectUsecase';
import ErrorUsecase from './usecase/ErrorUsecase';
import IntegrateAmazonAdAccountUsecase from './usecase/IntegrateAmazonAdAccountUsecase';
import IntegrateGoogleAdAccountUsecase from './usecase/IntegrateGoogleAdAccountUsecase';
import IntegrateTiktokAdAccountUsecase from './usecase/IntegrateTiktokAdAccountUsecase';
import IntegrateYahooAdAccountUsecase from './usecase/IntegrateYahooAdAccountUsecase';
import ListAdAnalyticsReportUsecase from './usecase/ListAdAnalyticsReportUsecase';
import ListAdAnalyticsVideoUsecase from './usecase/ListAdAnalyticsVideoUsecase';
import ListInsightCategoryUsecase from './usecase/ListInsightCategoryUsecase';
import ListProjectTypesUsecase from './usecase/ListProjectTypesUsecase';
import ListProjectUsecase from './usecase/ListProjectUsecase';
import ModificationRequestUsecase from './usecase/ModificationRequestUsecase';
import OrganizationUsecase from './usecase/OrganizationUsecase';
import ProjectDesignInspirationUsecase from './usecase/ProjectDesignInspirationUsecase';
import SelectDesignInspirationUsecase from './usecase/SelectDesignInspirationUsecase';
import SelectDirectionTypeUsecase from './usecase/SelectDirectionTypeUsecase';
import SendCreativeToFacebookUsecase from './usecase/SendCreativeToFacebookUsecase';
import SendCreativeToYoutubeUsecase from './usecase/SendCreativeToYoutubeUsecase';
import ShowAdAnalyticsReportUsecase from './usecase/ShowAdAnalyticsReportUsecase';
import ShowAdAnalyticsVideoUsecase from './usecase/ShowAdAnalyticsVideoUsecase';
import ShowCreationUsecase from './usecase/ShowCreationUsecase';
import ShowPerformanceReportUsecase from './usecase/ShowPerformanceReportUsecase';
import ShowProjectUsecase from './usecase/ShowProjectUsecase';
import createStoreConnectedAxios from './utility/createStoreConnectedAxios';
import getAccessingDevice from './utility/getAccessingDevice';
import Root from './view/Root';

declare global {
  interface Window {
    newrelic: typeof NewRelicBrowser;
    recordingUrl: string;
  }
}

// react-time-agoの初期化
TimeAgo.addLocale(ja);
TimeAgo.addLocale(en);

/**
 * 依存解決などのセットアップをし、コンポーネントのマウントを行うmain関数
 */
const main = () => {
  addLocaleData([...enLocaleData, ...jaLocaleData, { locale: 'ja-JP', parentLocale: 'ja' } as any]);

  const config: Config = (window as any).__hugotconfig__;
  const appVersion: string = (window as any).__hugotversion__;

  const initialState = {
    ...baseInitialState,
    location: {
      pathname: window.location.pathname,
      search: window.location.search,
    },
  };

  const history =
    getAccessingDevice() === 'sp'
      ? createBrowserHistory({
          getUserConfirmation: (dialogKey, callback) => {
            const dialogTrigger = (window as any)[dialogKey];

            if (dialogTrigger) {
              return dialogTrigger(callback);
            }

            callback(true);
          },
        })
      : createBrowserHistory();

  const store = new Store<State>(initialState);

  // --------------- 注入用の インフラ層(主にapi client) を作成 ---------------

  const graphqlClient = new GraphQLClient({
    endpointUrl: config.HUGOT_API_GRAPHQL_ENDPOINT,
  });
  const hugotGraphqlClient = (() => {
    const offerHttpLink = createHttpLink({
      uri: config.HUGOT_API_GRAPHQL_ENDPOINT,
      credentials: 'include',
    });
    // INFO: GraphQLがCacheを使うために`__typename`をObjectに追加するが、それがMutation時にエラーを引き起こすので（Schemaに含まれていないので）
    //   Linkとして毎回`__typename`を剥ぐように挙動をこちらから変更する。
    // 参照: https://github.com/apollographql/apollo-client/issues/1564#issuecomment-357492659
    const omitTypenameLink = new ApolloLink((operation, forward) => {
      if (operation.variables) {
        operation.variables = JSON.parse(JSON.stringify(operation.variables), (key, value) =>
          key === '__typename' ? undefined : value,
        );
      }
      return forward(operation);
    });
    return new ApolloClient({
      link: ApolloLink.from([omitTypenameLink, offerHttpLink]),
      cache: new InMemoryCache(),
    });
  })();

  const httpClient = new HttpClient({ baseUrl: config.CORE_API_BASE_URL });

  const hugotApiHttpClient = new HttpClient({
    baseUrl: config.HUGOT_API_BASE_URL,
  });

  const offerApiClient = (() => {
    const offerHttpLink = createHttpLink({
      uri: config.OFFER_API_GRAPHQL_ENDPOINT,
      credentials: 'include',
    });
    // INFO: GraphQLがCacheを使うために`__typename`をObjectに追加するが、それがMutation時にエラーを引き起こすので（Schemaに含まれていないので）
    //   Linkとして毎回`__typename`を剥ぐように挙動をこちらから変更する。
    // 参照: https://github.com/apollographql/apollo-client/issues/1564#issuecomment-357492659
    const omitTypenameLink = new ApolloLink((operation, forward) => {
      if (operation.variables) {
        operation.variables = JSON.parse(JSON.stringify(operation.variables), (key, value) =>
          key === '__typename' ? undefined : value,
        );
      }
      return forward(operation);
    });
    return new ApolloClient({
      link: ApolloLink.from([omitTypenameLink, offerHttpLink]),
      cache: new InMemoryCache(),
    });
  })();

  // --------------- 注入用の service(apiを叩く責務を持つもの) を作成 ---------------

  const acctApiHttpClient = new HttpClient({
    baseUrl: config.ACCT_API_BASE_URL,
  });

  const errorReporter = new ErrorReporter({
    store,
    dsn: config.SENTRY_API_DSN,
    environment: config.STAGE,
    release: appVersion,
    history: history,
  });
  window.newrelic.addRelease(config.STAGE, appVersion);
  window.onunhandledrejection = (data: any) => {
    errorReporter.reportError(data.reason);
  };

  const belfastClient = new BelfastClient({
    endpoint: config.BELFAST_API_BASE_URL,
  });
  const facebookMarketingApiConnectionService = new FacebookOauthService({
    resolveFb: () => (window as any).FB,
    appId: config.FACEBOOK_AD_APP_ID,
    scope: 'ads_management,business_management',
  });
  const googleOauthService = new GoogleOauthService({
    resolveGapi: () => (window as any).gapi,
    clientId: config.GOOGLE_AD_AUTH_CLIENT_ID,
    scope: 'https://www.googleapis.com/auth/adwords profile',
  });
  const youtubeChannelConnectionService = new GoogleOauthService({
    resolveGapi: () => (window as any).gapi,
    clientId: config.YOUTUBE_AUTH_CLIENT_ID,
    scope: 'https://www.googleapis.com/auth/youtube profile',
  });
  const fileHandler = new FileHandler();
  const logRocketService = new LogRocketService(LogRocket, appVersion);
  const intercomService = new IntercomService(() => (window as any).Intercom, config.INTERCOM_APP_ID);
  const messageDispatcher = new MessageDispatcher({ store });
  const translationBackend = new TranslationBackend({ appVersion });

  const configurationStorage = new ConfigurationStorage({
    storage: window.localStorage,
  });
  const viewVisibilityStorage = new ViewVisibilityStorage({
    storage: window.localStorage,
  });
  const adAnalyticsDailyPerformanceWebApi = new AdAnalyticsDailyPerformanceWebApi({ graphqlClient });
  const adAnalyticsVideoWebApi = new AdAnalyticsVideoWebApi({ graphqlClient });
  const adAnalyticsPlacementTypesWebApi = new AdAnalyticsPlacementTypesWebApi({
    graphqlClient,
  });
  const creativePerformanceCreativeWebApi = new CreativePerformanceCreativeWebApi({ graphqlClient });
  const adAnalyticsReportWebApi = new AdAnalyticsReportWebApi({ graphqlClient });
  const creationWebApi = new CreationWebApi({ graphqlClient });
  const creationSetWebApi = new CreationSetWebApi({ graphqlClient });
  const facebookAdWebApi = new FacebookAdWebApi({ graphqlClient });
  const googleAdWebApi = new GoogleAdWebApi({ graphqlClient });
  const youtubeWebApi = new YoutubeWebApi({ graphqlClient });
  const amazonAdWebApi = new AmazonAdWebApi({ graphqlClient });
  const yahooAdWebApi = new YahooAdWebApi({ graphqlClient });
  const tiktokAdWebApi = new TiktokAdWebApi({ graphqlClient });
  const modificationRequestWebApi = new ModificationRequestWebApi({
    graphqlClient,
  });
  const organizationWebApi = new OrganizationWebApi({
    graphqlClient,
    httpClient: createStoreConnectedAxios({ baseURL: config.CORE_API_BASE_URL }),
  });
  const projectWebApi = new ProjectWebApi({ graphqlClient });
  const projectChangeWebApi = new ProjectChangeWebApi({ graphqlClient });
  const projectTypeWebApi = new ProjectTypeWebApi({ graphqlClient });
  const userWebApi = new UserWebApi({ graphqlClient });
  const userRegistrationWebApi = new UserRegistrationWebApi({
    hugotApiHttpClient: createStoreConnectedAxios({
      baseURL: config.HUGOT_API_BASE_URL,
    }),
  });
  const performanceReportWebApi = new PerformanceReportWebApi({
    graphqlClient,
    httpClient: createStoreConnectedAxios({ baseURL: config.HUGOT_API_BASE_URL }),
  });
  const authenticationWebApi = new AuthenticationWebApi({ httpClient });
  const downloadApi = new DownloadApi({ httpClient: hugotApiHttpClient });

  // TODO: HttpClientとAxiosInstanceの違いを調べる
  const adSearchWebApi = new AdSearchWebApi({
    httpClient: createStoreConnectedAxios({
      baseURL: config.CREATIVE_API_BASE_URL,
    }),
  });

  const acctWebApi = new AcctWebApi({ httpClient: acctApiHttpClient });

  const searchAssetApi = new SearchAssetApi();

  const filesWebApi = new FilesWebApi();

  // --------------- 注入用のerrorPresenterを作成 ---------------

  const errorPresenter = new ErrorPresenter({ store, errorReporter });
  const apiErrorHandler = new ApiErrorHandler({ errorPresenter, errorReporter });

  // --------------- 注入用のusecase instanceを作成 ---------------

  const authenticationUsecase = new AuthenticationUsecase({
    store,
    history,
    authenticationWebApi,
    apiErrorHandler,
    errorPresenter,
    logRocketService,
    intercomService,
    organizationWebApi,
    userWebApi,
    userRegistrationWebApi,
  });
  const configurationChangingUsecase = new ConfigurationChangingUsecase({
    store,
    configurationStorage,
    translationBackend,
  });
  const modificationRequestUsecase = new ModificationRequestUsecase({
    store,
    history,
    belfastClient,
    messageDispatcher,
    errorPresenter,
    creationWebApi,
    modificationRequestWebApi,
    filesWebApi,
  });
  const deleteProjectUsecase = new DeleteProjectUsecase({
    store,
    messageDispatcher,
    projectWebApi,
  });
  const editProjectUsecase = new EditProjectUsecase({
    store,
    history,
    belfastClient,
    errorPresenter,
    messageDispatcher,
    projectWebApi,
    searchAssetApi,
  });
  const listProjectUsecase = new ListProjectUsecase({
    store,
    errorPresenter,
    organizationWebApi,
    projectWebApi,
  });
  const listProjectTypesUsecase = new ListProjectTypesUsecase({
    store,
    errorPresenter,
    intercomService,
    organizationWebApi,
    projectTypeWebApi,
  });
  const organizationUsecase = new OrganizationUsecase({
    store,
    organizationWebApi,
  });
  const showCreationUsecase = new ShowCreationUsecase({
    store,
    fileHandler,
    creationWebApi,
    creationSetWebApi,
    errorPresenter,
  });
  const showProjectUsecase = new ShowProjectUsecase({
    store,
    belfastClient,
    creationWebApi,
    projectWebApi,
    projectChangeWebApi,
    downloadApi,
    messageDispatcher,
    errorPresenter,
    hugotApiBaseUrl: config.HUGOT_API_BASE_URL,
  });
  const sendCreativeToFacebookUsecase = new SendCreativeToFacebookUsecase({
    store,
    errorPresenter,
    facebookMarketingApiConnectionService,
    messageDispatcher,
    facebookAdWebApi,
  });
  const integrateAmazonAdAccountUsecase = new IntegrateAmazonAdAccountUsecase({
    store,
    errorPresenter,
    messageDispatcher,
    amazonAdWebApi,
  });
  const integrateTiktokAdAccountUsecase = new IntegrateTiktokAdAccountUsecase({
    store,
    errorPresenter,
    messageDispatcher,
    tiktokAdWebApi,
    appId: config.TIKTOK_AD_APP_ID,
  });
  const integrateYahooAdAccountUsecase = new IntegrateYahooAdAccountUsecase({
    store,
    errorPresenter,
    messageDispatcher,
    yahooAdWebApi,
  });
  const integrateGoogleAdAccountUsecase = new IntegrateGoogleAdAccountUsecase({
    store,
    errorPresenter,
    googleOauthService,
    messageDispatcher,
    googleAdWebApi,
  });
  const sendCreativeToYoutubeUsecase = new SendCreativeToYoutubeUsecase({
    store,
    errorPresenter,
    messageDispatcher,
    youtubeChannelConnectionService,
    youtubeWebApi,
  });
  const showPerformanceReportUsecase = new ShowPerformanceReportUsecase({
    store,
    history,
    performanceReportWebApi,
    messageDispatcher,
    errorPresenter,
    viewVisibilityStorage,
  });
  const listAdAnalyticsVideoUsecase = new ListAdAnalyticsVideoUsecase({
    store,
    history,
    errorPresenter,
    adAnalyticsVideoWebApi,
    adAnalyticsPlacementTypesWebApi,
    creativePerformanceCreativeWebApi,
    organizationWebApi,
  });
  const showAdAnalyticsVideoUsecase = new ShowAdAnalyticsVideoUsecase({
    store,
    history,
    errorPresenter,
    messageDispatcher,
    adAnalyticsVideoWebApi,
    adAnalyticsPlacementTypesWebApi,
    adAnalyticsDailyPerformanceWebApi,
    organizationWebApi,
    adAnalyticsReportWebApi,
  });

  const listAdAnalyticsReportUsecase = new ListAdAnalyticsReportUsecase({
    store,
    history,
    errorPresenter,
    messageDispatcher,
    organizationWebApi,
    adAnalyticsReportWebApi,
  });

  const showAdAnalyticsReportUsecase = new ShowAdAnalyticsReportUsecase({
    store,
    history,
    errorPresenter,
    organizationWebApi,
    adAnalyticsReportWebApi,
    adAnalyticsVideoWebApi,
    adAnalyticsDailyPerformanceWebApi,
    adAnalyticsPlacementTypesWebApi,
  });

  const selectDirectionTypeUsecase = new SelectDirectionTypeUsecase({ store, organizationWebApi, errorPresenter });

  const selectDesignInspirationUsecase = new SelectDesignInspirationUsecase({
    store,
    organizationWebApi,
    errorPresenter,
  });

  const acctUsecase = new AcctUsecase({
    store,
    acctWebApi,
    errorPresenter,
  });

  const adSearchUsecase = new AdSearchUsecase({
    store,
    adSearchWebApi,
    errorPresenter,
    history,
  });

  const listInsightCategoryUsecase = new ListInsightCategoryUsecase({
    store,
    history,
    organizationWebApi,
    projectWebApi,
    errorPresenter,
  });

  const projectDesignInspirationUsecase = new ProjectDesignInspirationUsecase({
    store,
    history,
    projectWebApi,
    projectTypeWebApi,
  });

  const errorUsecase = new ErrorUsecase({ intercomService, messageDispatcher });

  // page componentにpropsとして渡す依存オブジェクトを作成
  const dependency: Dependency = {
    errorReporter,
    errorPresenter,
    messageDispatcher,
    authenticationUsecase,
    configurationChangingUsecase,
    modificationRequestUsecase,
    deleteProjectUsecase,
    editProjectUsecase,
    listProjectUsecase,
    listProjectTypesUsecase,
    organizationUsecase,
    sendCreativeToFacebookUsecase,
    integrateAmazonAdAccountUsecase,
    integrateGoogleAdAccountUsecase,
    integrateTiktokAdAccountUsecase,
    integrateYahooAdAccountUsecase,
    sendCreativeToYoutubeUsecase,
    showCreationUsecase,
    showProjectUsecase,
    showPerformanceReportUsecase,
    listAdAnalyticsVideoUsecase,
    showAdAnalyticsVideoUsecase,
    listAdAnalyticsReportUsecase,
    showAdAnalyticsReportUsecase,
    selectDirectionTypeUsecase,
    selectDesignInspirationUsecase,
    errorUsecase,
    adSearchUsecase,
    acctUsecase,
    listInsightCategoryUsecase,
    projectDesignInspirationUsecase,
    offerApiClient,
    graphqlClient: hugotGraphqlClient,
  };

  const container = document.getElementById('app'); // mount先を指定
  let isRendered = false;

  const update = (state: State) => {
    // 依存を注入したコンポーネントを作成
    const element = createElement(Root, { dependency, history, state, config });

    if (!isRendered) {
      isRendered = true;

      render(element, container);

      return;
    }

    hydrate(element, container);
  };

  history.listen((location) => {
    store.dispatch(setLocation(location));
    // send url-changed
    window.parent.postMessage('ad' + location.pathname.split('ad/').join('') + location.search + location.hash, '*');
  });
  // send loaded
  window.parent.postMessage('ad' + location.pathname.split('ad/').join('') + location.search + location.hash, '*');

  store.addMiddleware(createKukerEmitterMiddleware());

  // repatch storeが更新されるたびにDOMを更新するように登録
  store.subscribe(() => update(store.getState()));

  update(store.getState());

  // set language
  const language = configurationStorage.loadLanguage() || window.navigator.language;
  configurationChangingUsecase.changeLanguageTemporarily(language);
};

window.document.addEventListener('DOMContentLoaded', main);
